import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Message } from '@/types/chat';
import { useToast } from '@/hooks/use-toast';

export const useMessages = (chatId: string) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchMessages = useCallback(async () => {
    if (!chatId) return;

    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('chat_id', chatId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Get sender profiles separately
      const senderIds = [...new Set(data?.map(msg => msg.sender_id) || [])];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username, name, image')
        .in('id', senderIds);

      const formattedMessages: Message[] = data?.map((msg: any) => ({
        ...msg,
        type: msg.type as 'text' | 'share',
        share_payload: msg.share_payload as Message['share_payload'],
        sender: profiles?.find(p => p.id === msg.sender_id)
      })) || [];
      
      setMessages(formattedMessages);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast({
        title: 'Error',
        description: 'Failed to load messages',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [chatId, toast]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Subscribe to realtime message changes
  useEffect(() => {
    if (!chatId) return;

    const channel = supabase
      .channel(`messages-${chatId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `chat_id=eq.${chatId}`,
        },
        async (payload) => {
          // Fetch the new message with sender info
          const { data: messageData } = await supabase
            .from('messages')
            .select('*')
            .eq('id', payload.new.id)
            .single();

          if (messageData) {
            const { data: senderProfile } = await supabase
              .from('profiles')
              .select('id, username, name, image')
              .eq('id', messageData.sender_id)
              .single();

            const formattedMessage: Message = {
              ...messageData,
              type: messageData.type as 'text' | 'share',
              share_payload: messageData.share_payload as Message['share_payload'],
              sender: senderProfile
            };
            setMessages((prev) => [...prev, formattedMessage]);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [chatId]);

  const sendMessage = async (text: string, sharePayload?: any) => {
    if (!user || !chatId) return;

    try {
      const messageData = {
        chat_id: chatId,
        sender_id: user.id,
        type: sharePayload ? 'share' : 'text',
        text: sharePayload ? undefined : text,
        share_payload: sharePayload,
      };

      const { error } = await supabase
        .from('messages')
        .insert([messageData]);

      if (error) throw error;

      // Also create a share record if it's a share message
      if (sharePayload) {
        await supabase.from('shares').insert([{
          chat_id: chatId,
          sender_id: user.id,
          kind: sharePayload.kind,
          book_id: sharePayload.book_id,
          review_id: sharePayload.review_id,
        }]);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: 'Error',
        description: 'Failed to send message',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const markAsRead = async () => {
    if (!user || !chatId) return;

    try {
      await supabase
        .from('chat_members')
        .update({ last_read_at: new Date().toISOString() })
        .eq('chat_id', chatId)
        .eq('user_id', user.id);
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  return {
    messages,
    loading,
    sendMessage,
    markAsRead,
    refetch: fetchMessages,
  };
};